import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock, faEye, faXmark } from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import dayjs from "dayjs";
import { Building2, ChevronRight, Zap, Clock, FolderOpen, Sparkles } from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";

import OverTimeFilter from "./OverTimeFilter";
import Pagination from "../../../components/common/Pagination";
import OverTimeDetailModal from "./OverTimeDetailModal";
import OverTimeCreateModal from "./OverTimeCreateModal";
import PendingOvertimeList from "./PendingOvertimeList";
import RunningOvertimeList from "./RunningOvertimeList";
import WaitingReviewList from "./WaitingReviewList";
// eslint-disable-next-line no-unused-vars -- exportOvertimeCsv: API CSV legacy; UI xuất Excel qua getAllOvertimes
import { getAllOvertimes, exportOvertimeCsv } from "../../../api/hrm/employees/overtimes";
import { hasPermission } from "../../../utils/permissionUtils";
import { formatDateVN } from "../../../utils/formatUtils";
import { downloadHrmListingExcel } from "../hrmListingExportExcel";
import { showSuccessToast, showErrorToast } from "../../../components/ConfirmDialog";
import { startOvertimeListTour } from "./overtimeListTour";

function formatOvertimeStatusLabel(s) {
  switch (s) {
    case "pending":
      return "Đăng ký";
    case "waiting_level_2":
      return "Đang duyệt";
    case "approved":
      return "Phê duyệt";
    case "waiting_report":
      return "Chờ báo cáo";
    case "completed":
      return "Hoàn thành";
    case "waiting_review":
      return "Chờ đánh giá";
    case "cancelled":
      return "Đã hủy";
    case "rejected":
      return "Không duyệt";
    default:
      return s || "-";
  }
}

const OverTimeListPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const storedUser = localStorage.getItem("user");
  const currentUser = storedUser ? JSON.parse(storedUser) : null;
  const notificationTarget =
    location.state?.notificationTarget?.moduleCode === "hrm.overtime"
      ? location.state.notificationTarget
      : null;

  const clearNotificationTarget = () => {
    navigate(location.pathname, { replace: true, state: null });
  };

  const [filters, setFilters] = useState({});
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showDrawer, setShowDrawer] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create");
  const [editData, setEditData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [dailyChart, setDailyChart] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);

  const [refreshFlag, setRefreshFlag] = useState(0);
  const triggerRefresh = () => setRefreshFlag((prev) => prev + 1);

  const runIdRef = useRef(`ot_pre_${Date.now()}`);
  const reqSeqRef = useRef(0);
  const latestReqIdRef = useRef(0);

  // =========================================================
  // 🔥 GET LIST OT (giữ nguyên + dùng limit)
  // =========================================================
  const fetchData = async (page = currentPage, size = pageSize) => {
    const reqId = ++reqSeqRef.current;
    latestReqIdRef.current = reqId;

    // #region agent log fetchData start
    fetch("http://127.0.0.1:7317/ingest/a9e47ab1-61a3-4775-b050-0facfebdbb65", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Debug-Session-Id": "0dfe12",
      },
      body: JSON.stringify({
        sessionId: "0dfe12",
        runId: runIdRef.current,
        hypothesisId: "H2_race_or_stale_filters",
        location: "src/modules/hrm/overtime/OverTimeListPage.jsx:fetchData:start",
        message: "OT list fetch start",
        data: {
          reqId,
          page,
          limit: size,
          from_date: filters?.from_date,
          to_date: filters?.to_date,
          keyword: filters?.keyword,
          branch_id: filters?.branch_id,
          department_id: filters?.department_id,
          status: filters?.status,
        },
        timestamp: Date.now(),
      }),
    }).catch(() => {});
    // #endregion

    try {
      if (!filters.from_date || !filters.to_date) return;
      const res = await getAllOvertimes({
        ...filters,
        page,
        limit: size,
      });

      // #region agent log fetchData response
      fetch("http://127.0.0.1:7317/ingest/a9e47ab1-61a3-4775-b050-0facfebdbb65", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Debug-Session-Id": "0dfe12",
        },
        body: JSON.stringify({
          sessionId: "0dfe12",
          runId: runIdRef.current,
          hypothesisId: "H2_race_or_stale_filters",
          location:
            "src/modules/hrm/overtime/OverTimeListPage.jsx:fetchData:response",
          message: "OT list fetch response",
          data: {
            reqId,
            latestReqIdAtResponse: latestReqIdRef.current,
            recordsCount: res?.records?.length ?? 0,
            total: res?.total ?? 0,
          },
          timestamp: Date.now(),
        }),
      }).catch(() => {});
      // #endregion

      // #region agent log ignore stale response
      if (reqId !== latestReqIdRef.current) {
        fetch("http://127.0.0.1:7317/ingest/a9e47ab1-61a3-4775-b050-0facfebdbb65", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Debug-Session-Id": "0dfe12",
          },
          body: JSON.stringify({
            sessionId: "0dfe12",
            runId: runIdRef.current,
            hypothesisId: "H2_race_or_stale_filters",
            location:
              "src/modules/hrm/overtime/OverTimeListPage.jsx:fetchData:ignoreStale",
            message: "OT ignore stale response",
            data: {
              reqId,
              latestReqIdRefCurrent: latestReqIdRef.current,
              resRecordsCount: res?.records?.length ?? 0,
              resTotal: res?.total ?? 0,
            },
            timestamp: Date.now(),
          }),
        }).catch(() => {});
        // #endregion
        return;
      }
      // #endregion

      setData(res.records || []);
      setTotalRecords(res.total || 0);
    } catch (err) {
      console.error("❌ Lỗi tải danh sách tăng ca:", err);
    }
  };

  const normalizeDateKey = (value) => {
    if (!value) return "";
    const s = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const buildRangeDays = (fromDate, toDate) => {
    if (!fromDate || !toDate) return [];
    const from = dayjs(fromDate);
    const to = dayjs(toDate);
    if (!from.isValid() || !to.isValid() || to.isBefore(from, "day")) return [];
    const days = [];
    let cursor = from.startOf("day");
    const end = to.startOf("day");
    while (cursor.isBefore(end) || cursor.isSame(end, "day")) {
      days.push(cursor.format("YYYY-MM-DD"));
      cursor = cursor.add(1, "day");
    }
    return days;
  };

  const fetchDailyOvertimeChart = async () => {
    if (!filters?.from_date || !filters?.to_date) {
      setDailyChart([]);
      return;
    }
    setChartLoading(true);
    try {
      const limit = 500;
      const firstPage = await getAllOvertimes({
        ...filters,
        page: 1,
        limit,
      });
      const firstRecords = firstPage?.records || [];
      const total = Number(firstPage?.total || firstRecords.length || 0);
      const totalPages = Math.max(1, Math.ceil(total / limit));

      let allRecords = [...firstRecords];
      if (totalPages > 1) {
        const rest = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, idx) =>
            getAllOvertimes({
              ...filters,
              page: idx + 2,
              limit,
            })
          )
        );
        rest.forEach((res) => {
          allRecords = allRecords.concat(res?.records || []);
        });
      }

      // API đã áp dụng data_scope theo user; FE chỉ aggregate từ tập dữ liệu đã được phép truy cập
      const bucket = new Map();
      allRecords.forEach((item) => {
        const key = normalizeDateKey(item.work_date);
        if (!key) return;
        const prev = bucket.get(key) || { minutes: 0, count: 0 };
        const minutes = Number(item.ot_minutes || 0);
        bucket.set(key, {
          minutes: prev.minutes + (Number.isFinite(minutes) ? minutes : 0),
          count: prev.count + 1,
        });
      });

      const dayKeys = buildRangeDays(filters.from_date, filters.to_date);
      const chartRows = dayKeys.map((dayKey) => {
        const agg = bucket.get(dayKey) || { minutes: 0, count: 0 };
        return {
          dateKey: dayKey,
          dayLabel: dayjs(dayKey).format("DD/MM"),
          otHours: Number((agg.minutes / 60).toFixed(2)),
          otMinutes: agg.minutes,
          records: agg.count,
        };
      });
      setDailyChart(chartRows);
    } catch (err) {
      console.error("❌ Lỗi tải chart tăng ca theo ngày:", err);
      setDailyChart([]);
    } finally {
      setChartLoading(false);
    }
  };

  // =========================================================
  // Khởi tạo filter đầu tháng
  // =========================================================
  useEffect(() => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    setFilters({
      keyword: "",
      branch_id: "",
      department_id: "",
      from_date: dayjs(first).format("YYYY-MM-DD"),
      to_date: dayjs(last).format("YYYY-MM-DD"),
      status: "",
    });
  }, []);

  // Reload on filter change
  useEffect(() => {
    if (filters.from_date && filters.to_date) {
      fetchData();
      fetchDailyOvertimeChart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, currentPage, pageSize]);

  const handleFilterChange = (newFilters) => {
    // #region agent log handleFilterChange
    fetch("http://127.0.0.1:7317/ingest/a9e47ab1-61a3-4775-b050-0facfebdbb65", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Debug-Session-Id": "0dfe12",
      },
      body: JSON.stringify({
        sessionId: "0dfe12",
        runId: runIdRef.current,
        hypothesisId: "H1_filter_state_update",
        location: "src/modules/hrm/overtime/OverTimeListPage.jsx:handleFilterChange",
        message: "OT handleFilterChange called",
        data: {
          from_date: newFilters?.from_date,
          to_date: newFilters?.to_date,
          keyword: newFilters?.keyword,
          branch_id: newFilters?.branch_id,
          department_id: newFilters?.department_id,
          status: newFilters?.status,
        },
        timestamp: Date.now(),
      }),
    }).catch(() => {});
    // #endregion

    setFilters(newFilters);
    setCurrentPage(1);
  };

  // =========================================================
  // 🔥 EXPORT EXCEL (cùng kiểu định dạng với Chấm công)
  // =========================================================
  const parseMonthKey = (ym) => {
    if (!ym || typeof ym !== "string" || !/^\d{4}-\d{2}$/.test(ym)) return null;
    const [yStr, mStr] = ym.split("-");
    const year = parseInt(yStr, 10);
    const month = parseInt(mStr, 10);
    if (Number.isNaN(year) || Number.isNaN(month) || month < 1 || month > 12)
      return null;

    const pad = (n) => String(n).padStart(2, "0");
    const lastDay = new Date(year, month, 0).getDate();
    const monthPadded = pad(month);

    return {
      year,
      month,
      monthPadded,
      from_date: `${year}-${monthPadded}-01`,
      to_date: `${year}-${monthPadded}-${pad(lastDay)}`,
    };
  };

  const handleDownloadExcel = async ({
    branch_id,
    month,
    branch_label,
  } = {}) => {
    try {
      const parsedMonth = month ? parseMonthKey(month) : null;
      const from_date = parsedMonth ? parsedMonth.from_date : filters.from_date;
      const to_date = parsedMonth ? parsedMonth.to_date : filters.to_date;

      if (!from_date || !to_date) {
        showErrorToast("Chọn khoảng thời gian (từ ngày – đến ngày).");
        return;
      }

      const query = {
        ...filters,
        page: 1,
        limit: 10000,
        from_date,
        to_date,
      };
      if (branch_id) query.branch_id = branch_id;

      const res = await getAllOvertimes(query);
      const records = res.records || [];
      if (records.length === 0) {
        showErrorToast("Không có dữ liệu để xuất.");
        return;
      }

      const header = [
        "STT",
        "Chi nhánh",
        "Bộ phận",
        "Nhân viên",
        "Ngày tăng ca",
        "Từ giờ",
        "Đến giờ",
        "Số giờ",
        "Lý do",
        "Trạng thái",
      ];
      const rows = records.map((r, i) => [
        i + 1,
        r.branch_name ?? "",
        r.department_name ?? "",
        r.employee_name ?? "",
        formatDateVN(r.work_date),
        r.ot_from?.slice(0, 5) ?? "",
        r.ot_to?.slice(0, 5) ?? "",
        r.ot_minutes != null ? (r.ot_minutes / 60).toFixed(1) : "0.0",
        r.reason ?? "",
        formatOvertimeStatusLabel(r.status),
      ]);
      const aoa = [header, ...rows];

      const branchText =
        branch_label?.trim() ||
        (branch_id ? "Chi nhánh" : "Tất cả chi nhánh");
      const branchTag = branch_id ? String(branch_id).slice(0, 8) : "tat-ca";

      const title = parsedMonth
        ? `Dữ liệu tăng ca tháng ${parsedMonth.monthPadded} năm ${parsedMonth.year} của ${branchText}`
        : `Dữ liệu tăng ca từ ${formatDateVN(from_date)} đến ${formatDateVN(to_date)}`;

      const filename = parsedMonth
        ? `tang-ca_${month}_${branchTag}.xlsx`
        : `tang-ca_${from_date}_${to_date}_${branchTag}.xlsx`;

      await downloadHrmListingExcel(aoa, {
        title,
        sheetName: "TangCa",
        filename,
        columnWidths: [6, 22, 20, 24, 16, 10, 10, 10, 40, 18],
      });
      showSuccessToast("Đã tải file Excel.");
    } catch (error) {
      console.error("❌ Export Excel fail:", error);
      showErrorToast("Không thể xuất Excel.");
    }
  };

  // Pagination
  const handlePageChange = (p) => setCurrentPage(p);
  const handlePageSizeChange = (s) => {
    setPageSize(s);
    setCurrentPage(1);
  };

  const formatStatus = (s) => {
    const baseClass =
      "inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border";

    switch (s) {
      case "pending":
      case "waiting_level_2":
        return (
          <span
            className={`${baseClass} bg-orange-50 text-orange-700 border-orange-100`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mr-1.5 animate-pulse" />
            {s === "pending" ? "Đăng ký" : "Đang duyệt"}
          </span>
        );
      case "approved":
      case "waiting_report":
        return (
          <span
            className={`${baseClass} bg-green-50 text-green-700 border-green-100`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5" />
            {s === "approved" ? "Phê duyệt" : "Chờ báo cáo"}
          </span>
        );
      case "completed":
      case "waiting_review":
        return (
          <span
            className={`${baseClass} bg-blue-50 text-blue-700 border-blue-100`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mr-1.5" />
            {s === "completed" ? "Hoàn thành" : "Chờ đánh giá"}
          </span>
        );
      case "cancelled":
      case "rejected":
        return (
          <span
            className={`${baseClass} bg-red-50 text-red-700 border-red-100`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 mr-1.5" />
            {s === "cancelled" ? "Đã hủy" : "Không duyệt"}
          </span>
        );
      default:
        return <span className={baseClass}>{s || "-"}</span>;
    }
  };

  const waitingReport = data.filter((r) => r.status === "waiting_report");
  const reviewReport = data.filter((r) => r.status === "waiting_review");

  // ========================================================================
  // UI PAGE BEGIN
  // ========================================================================
  return (
    <div className="p-4">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-8" data-tour="hrm-overtime-header">
        <div className="flex items-center gap-4">
          <div className="bg-red-700 p-2.5 rounded-2xl text-white shadow-lg shadow-red-200">
            <FontAwesomeIcon
              icon={faClock}
              className="text-xl w-6 h-6 flex items-center justify-center"
            />
          </div>

          <div>
            <h1 className="text-xl font-black text-gray-800 tracking-tight uppercase">
              Quản lý <span className="text-red-700">tăng ca</span>
            </h1>

            <div className="flex items-center gap-2 mt-0.5">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
              <p className="text-[11px] text-gray-500 font-bold uppercase tracking-wider text-nowrap">
                <span className="text-red-600">{totalRecords}</span>{" "} phiếu tăng ca đã đăng ký
              </p>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => window.setTimeout(() => startOvertimeListTour(), 80)}
          title="Hướng dẫn nhanh"
          aria-label="Hướng dẫn nhanh"
          className="group relative inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-red-200 bg-white text-red-700 shadow-sm transition-all hover:bg-red-50 hover:border-red-300"
        >
          <Sparkles className="h-5 w-5" strokeWidth={2.2} />
          <span className="pointer-events-none absolute bottom-full right-0 mb-2 rounded-lg border border-red-100 bg-white px-2.5 py-1 text-[11px] font-bold text-red-700 whitespace-nowrap opacity-0 translate-y-1 shadow-md transition-all group-hover:opacity-100 group-hover:translate-y-0">
            Hướng dẫn nhanh
          </span>
        </button>
      </div>

      {/* 🔍 FILTER + CREATE */}
      <div data-tour="hrm-overtime-filter">
        <OverTimeFilter
          filters={filters}
          onNewClick={() => {
            setDrawerMode("create");
            setEditData(null);
            setShowDrawer(true);
          }}
          onFilterChange={handleFilterChange}
          onDownloadCsv={handleDownloadExcel}
        />
      </div>

      <div className="mb-6 rounded-[2rem] border border-gray-100 bg-white p-5 shadow-sm" data-tour="hrm-overtime-chart">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="text-sm font-black uppercase tracking-wide text-gray-700">
            Biểu đồ thời gian tăng ca theo ngày
          </h3>
          <span className="text-[11px] font-bold text-gray-400">
            {filters?.from_date && filters?.to_date
              ? `${formatDateVN(filters.from_date)} - ${formatDateVN(filters.to_date)}`
              : "Chưa chọn khoảng ngày"}
          </span>
        </div>
        <div className="h-[260px] w-full">
          {chartLoading ? (
            <div className="flex h-full items-center justify-center text-sm font-semibold text-gray-400">
              Đang tải dữ liệu biểu đồ...
            </div>
          ) : dailyChart.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm font-semibold text-gray-400">
              Không có dữ liệu tăng ca trong khoảng ngày đã chọn.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dailyChart} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis
                  dataKey="dayLabel"
                  tick={{ fontSize: 11, fill: "#94a3b8", fontWeight: 700 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#94a3b8", fontWeight: 700 }}
                  axisLine={false}
                  tickLine={false}
                  width={36}
                />
                <Tooltip
                  formatter={(value, name, payload) => {
                    if (name === "otHours") return [`${value} giờ`, "Thời gian OT"];
                    return [value, name];
                  }}
                  labelFormatter={(label, entries) => {
                    const row = entries?.[0]?.payload;
                    const count = row?.records ?? 0;
                    return `${label} (${count} phiếu)`;
                  }}
                  cursor={{ fill: "#fef2f2" }}
                />
                <Bar
                  dataKey="otHours"
                  name="otHours"
                  fill="#dc2626"
                  radius={[6, 6, 0, 0]}
                  maxBarSize={26}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* ===================================================================
        1) PHIẾU CHỜ DUYỆT (pending + waiting_level_2)
      ====================================================================*/}
      <div data-tour="hrm-overtime-pending">
      <PendingOvertimeList
        filters={filters}
        refreshFlag={refreshFlag}
        autoOpenId={notificationTarget?.targetType === "pending" ? notificationTarget.referenceId : null}
        onAutoOpened={clearNotificationTarget}
        onChanged={() => {
          triggerRefresh(); // UI con reload
          fetchData(); // bảng chính reload
        }}
      />
      </div>

      {/* ===================================================================
        2) PHIẾU ĐÃ DUYỆT – ĐANG THỰC HIỆN (waiting_report)
      ====================================================================*/}
      <div data-tour="hrm-overtime-running">
      <RunningOvertimeList
        filters={filters}
        refreshFlag={refreshFlag}
        autoOpenId={notificationTarget?.targetType === "running" ? notificationTarget.referenceId : null}
        onAutoOpened={clearNotificationTarget}
        onChanged={() => {
          triggerRefresh(); // UI con reload
          fetchData(); // bảng chính reload
        }}
      />
      </div>

      {/* ===================================================================
        3) PHIẾU ĐÃ BÁO CÁO – CHỜ REVIEW (waiting_review)
      ====================================================================*/}
      <div data-tour="hrm-overtime-review">
      <WaitingReviewList
        filters={filters}
        refreshFlag={refreshFlag}
        autoOpenId={notificationTarget?.targetType === "review" ? notificationTarget.referenceId : null}
        onAutoOpened={clearNotificationTarget}
        onChanged={() => {
          triggerRefresh(); // UI con reload
          fetchData(); // bảng chính reload
        }}
      />
      </div>

      {/* ===================================================================
        4) TABLE – GIỮ NGUYÊN
      ====================================================================*/}
      <h2 className="font-semibold text-base text-gray-700 mb-3 mt-6 ml-2">
        Bảng danh sách phiếu tăng ca ({totalRecords} phiếu)
      </h2>

      <div className="bg-white rounded-[2rem] shadow-xl shadow-slate-200/50 border border-gray-100 overflow-hidden" data-tour="hrm-overtime-table">
  <div className="overflow-x-auto">
    <table className="w-full table-auto border-collapse border-separate border-spacing-0">
      <thead>
        <tr className="bg-slate-50/80 backdrop-blur-sm border-b border-gray-100 text-left">
          <th className="p-5 w-[60px] text-center text-[11px] font-black text-slate-400 uppercase tracking-widest border-b border-gray-100">
            #
          </th>
          {/* ✅ Kết hợp thành một cột duy nhất */}
          <th className="p-5 min-w-[280px] text-[11px] font-black text-slate-400 uppercase tracking-widest border-b border-gray-100">
            Thông tin nhân viên
          </th>
          <th className="p-5 min-w-[140px] text-center text-[11px] font-black text-slate-400 uppercase tracking-widest border-b border-gray-100">
            Ngày tăng ca
          </th>
          <th className="p-5 min-w-[180px] text-center text-[11px] font-black text-slate-400 uppercase tracking-widest border-b border-gray-100">
            Khung giờ OT
          </th>
          <th className="p-5 min-w-[100px] text-center text-[11px] font-black text-slate-400 uppercase tracking-widest border-b border-gray-100">
            Số giờ
          </th>
          <th className="p-5 min-w-[250px] text-[11px] font-black text-slate-400 uppercase tracking-widest border-b border-gray-100">
            Lý do tăng ca
          </th>
          <th className="p-5 min-w-[140px] text-center text-[11px] font-black text-slate-400 uppercase tracking-widest border-b border-gray-100">
            Trạng thái
          </th>
        </tr>
      </thead>

      <tbody className="divide-y divide-gray-50">
        {data.length === 0 ? (
          <tr>
            {/* ✅ Cập nhật colSpan xuống 7 */}
            <td colSpan={7} className="p-20 text-center">
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mb-4">
                  <Clock size={32} className="text-gray-200" />
                </div>
                <p className="text-gray-400 font-black uppercase tracking-widest text-xs">
                  Hiện chưa có đơn đăng ký tăng ca nào.
                </p>
              </div>
            </td>
          </tr>
        ) : (
          data.map((item, index) => (
            <tr
              key={item.id}
              onClick={() => setSelected(item)}
              className="hover:bg-red-50/40 transition-all duration-300 group cursor-pointer"
            >
              <td className="p-5 text-xs text-slate-300 text-center font-black">
                {((currentPage - 1) * pageSize + index + 1).toString().padStart(2, '0')}
              </td>
              
              {/* ✅ Hiển thị kết hợp Nhân viên & Chi nhánh */}
              <td className="p-5">
                <div className="flex items-center gap-4">
                  {/* Avatar xử lý theo tên chính (chữ cuối) */}
                  <div className="w-11 h-11 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center font-black text-xs shrink-0 border border-red-100 shadow-sm">
                    {item.employee_name ? item.employee_name.trim().split(" ").pop().charAt(0).toUpperCase() : "?"}
                  </div>
                  <div className="flex flex-col min-w-0">
                    <div className="font-black text-slate-700 group-hover:text-red-600 transition-colors text-sm uppercase tracking-tight truncate">
                      {item.employee_name}
                    </div>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                        {item.branch_name}
                      </span>
                      <span className="text-slate-200 text-[10px]">•</span>
                      <span className="text-[10px] font-bold text-slate-400 italic">
                        {item.department_name || "Hệ thống"}
                      </span>
                    </div>
                  </div>
                </div>
              </td>

              <td className="p-5 text-center">
                <span className="inline-block px-3 py-1.5 bg-slate-100 rounded-lg text-[10px] font-black text-slate-500 tracking-tight">
                  {formatDateVN(item.work_date)}
                </span>
              </td>

              <td className="p-5">
                <div className="flex items-center justify-center gap-2">
                  <div className="text-center">
                    <div className="text-[9px] text-slate-400 font-black uppercase tracking-widest">Từ</div>
                    <div className="text-sm font-black text-slate-700">
                      {item.ot_from?.slice(0, 5) || "--:--"}
                    </div>
                  </div>
                  <div className="text-slate-300 font-black">→</div>
                  <div className="text-center">
                    <div className="text-[9px] text-slate-400 font-black uppercase tracking-widest">Đến</div>
                    <div className="text-sm font-black text-slate-700">
                      {item.ot_to?.slice(0, 5) || "--:--"}
                    </div>
                  </div>
                </div>
              </td>

              <td className="p-5 text-center">
                <div className="inline-flex items-center justify-center px-4 py-2 bg-emerald-50 text-emerald-600 rounded-2xl border border-emerald-100 font-black text-sm shadow-sm">
                  {item.ot_minutes ? (item.ot_minutes / 60).toFixed(1) : "0.0"}
                </div>
              </td>

              <td className="p-5">
                <p className="text-xs text-slate-500 italic font-medium line-clamp-2 leading-relaxed" title={item.reason}>
                  "{item.reason || "-"}"
                </p>
              </td>

              <td className="p-5 text-center">
                {formatStatus(item.status)}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
</div>

      <Pagination
        totalRecords={totalRecords}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {/* Drawer tạo mới */}
      {hasPermission("hrm.overtime", "C") && (
        <OverTimeCreateModal
          isOpen={showDrawer}
          onClose={() => setShowDrawer(false)}
          onSaved={() => {
            setRefreshFlag((prev) => prev + 1);
            fetchData();
            setShowDrawer(false);
          }}
          mode={drawerMode}
          editData={editData}
        />
      )}

      {/* Popup xem chi tiết */}
      <OverTimeDetailModal
        isOpen={!!selected}
        data={selected}
        onClose={() => setSelected(null)}
      />
    </div>
  );
};

export default OverTimeListPage;
